import { api } from './index';

import type { Document as DocumentType } from '@/types';

export interface DocumentsApiOptions {
  limit?: number;
  page?: number;
  search?: string;
  sharedWith?: string[];
  sharedWithMe?: boolean;
  template?: boolean;
  userId?: string;
}

export interface DocumentsResponse {
  items: DocumentType[];
  pages: number;
  total: number;
}

export const documentApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getDocuments: builder.query<DocumentsResponse, DocumentsApiOptions>({
      query: (params) => ({
        method: 'GET',
        url: '/documents',
        params,
      }),
      providesTags: ['Document'],
    }),
    getDocument: builder.query<DocumentType, string>({
      query: (id) => ({ method: 'GET', url: `/documents/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'Document', id }],
    }),
    createDocument: builder.mutation<
      DocumentType,
      Partial<DocumentType> & { sharedWith?: string[]; template?: boolean }
    >({
      query: (document) => ({
        url: '/documents',
        method: 'POST',
        data: document,
      }),
      invalidatesTags: ['Document'],
    }),
    updateDocument: builder.mutation<
      DocumentType,
      Partial<DocumentType> & { sharedWith?: string[] }
    >({
      query: ({ id, sharedWith, ...document }) => ({
        url: `/documents/${id}`,
        method: 'PATCH',
        data: {
          document: { id, ...document },
          sharedWith,
        },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Document', id },
        'Document',
      ],
    }),
    deleteDocument: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/documents/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Document', id },
        'Document',
      ],
    }),
    duplicateDocument: builder.mutation<
      Document,
      { id: string; name?: string; userId?: string }
    >({
      query: (params) => ({
        url: '/documents/duplicate',
        method: 'POST',
        data: params,
      }),
      invalidatesTags: ['Document'],
    }),
    uploadDocument: builder.mutation<DocumentType, { file: File; userId: string }>({
      query: ({ file, userId }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('userId', userId);
        
        return {
          url: '/documents/upload',
          method: 'POST',
          data: formData,
        };
      },
      invalidatesTags: ['Document'],
    }),
  }),
});

export const {
  useGetDocumentsQuery,
  useGetDocumentQuery,
  useCreateDocumentMutation,
  useUpdateDocumentMutation,
  useDeleteDocumentMutation,
  useDuplicateDocumentMutation,
  useUploadDocumentMutation,
} = documentApi;
